export interface Player {
  id: number;
  name: string;
  country: string; // We'll use the logo URL here for now since MatchTile supports image URLs
}

export interface Score {
  home: number[];
  away: number[];
  liveGameScore?: string;
}

export interface Fixture {
  id: number;
  tournamentName: string;
  tournamentKey?: string;
  round?: string;
  season?: string;
  category: 'ATP' | 'ITF' | 'WTA' | 'Exibição' | 'Outros';
  matchCategory: 'Simples Masculino' | 'Simples Feminino' | 'Duplas Masculinas' | 'Duplas Femininas' | 'Duplas Mistas' | 'Copa por Equipes' | 'Masculino Juvenil' | 'Feminino Juvenil' | 'Outros';
  status: string;
  date?: string;
  homePlayer: { id: number; name: string; country?: string | string[]; logo?: string };
  awayPlayer: { id: number; name: string; country?: string | string[]; logo?: string };
  score: Score;
  liveStats?: {
    currentSet: number;
    homeAces: number;
    awayAces: number;
    homeDoubleFaults: number;
    awayDoubleFaults: number;
    homeBreakPoints: number;
    awayBreakPoints: number;
  };
}

export const TennisLiveApi = {
  getApiKey(): string {
    const key = import.meta.env.VITE_TENNIS_API_KEY || '8fcfd295623672a53b9c58b8ea97ecca369965a2f5d170a5e81b03ad6cca25e7';
    return key;
  },

  getBaseUrl(): string {
    return 'https://api.api-tennis.com/tennis/';
  },

  playersMap: {} as Record<string, {c: string, n: string}>,
  mapLoaded: false,

  async initPlayersMap() {
    if (this.mapLoaded) return;
    try {
      const apiUrl = import.meta.env.VITE_API_URL || 'https://techtennis-api.vercel.app';
      const response = await fetch(`${apiUrl}/api/tennis/players-map`);
      if (response.ok) {
        this.playersMap = await response.json();
        this.mapLoaded = true;
      }
    } catch (e) {
      console.error('Failed to load players map', e);
    }
  },

  async _fetch(method: string, params: Record<string, string> = {}): Promise<any> {
    if (!this.mapLoaded) {
      await this.initPlayersMap();
    }
    const url = new URL(this.getBaseUrl());
    url.searchParams.append('method', method);
    url.searchParams.append('APIkey', this.getApiKey());
    url.searchParams.append('timezone', 'America/Sao_Paulo');
    Object.entries(params).forEach(([key, value]) => {
      url.searchParams.append(key, value);
    });

    try {
      const response = await fetch(url.toString());
      if (!response.ok) return { success: 0, result: [] };
      return await response.json();
    } catch (error) {
      console.error(`Tennis API Error [${method}]:`, error);
      return { success: 0, result: [] };
    }
  },

  _getFlagUrl(playerKey: string | number, fallbackLogoUrl: string, playerName?: string): string | string[] {
    const pk = playerKey?.toString();
    
    if (playerName && playerName.includes('/')) {
      const names = playerName.split('/').map(n => n.trim());
      const allPlayers = Object.values(this.playersMap);
      return names.map(n => {
        if (!n) return '';
        const found = allPlayers.find(p => p.n && p.n.toLowerCase().includes(n.toLowerCase()));
        if (found) {
          let code = found.c.toLowerCase();
          if (code === 'uk') code = 'gb';
          return `https://flagcdn.com/24x18/${code}.png`;
        }
        return '';
      });
    }

    if (pk && this.playersMap[pk]) {
      let code = this.playersMap[pk].c.toLowerCase();
      if (code === 'uk') code = 'gb';
      return `https://flagcdn.com/24x18/${code}.png`;
    }

    if (!fallbackLogoUrl) return '';
    const match = fallbackLogoUrl.match(/logo-country\/(?:.*?_)?([a-z]{2,3})\.png/i);
    if (match) {
      let code = match[1].toLowerCase();
      if (code === 'uk') code = 'gb'; // flagcdn uses gb for UK
      return `https://flagcdn.com/24x18/${code}.png`;
    }
    return '';
  },

  _detectCategory(type: string): 'ATP' | 'WTA' | 'ITF' | 'Exibição' | 'Outros' {
    if (!type) return 'ATP';
    const t = type.toLowerCase();
    if (t.includes('exhibition')) return 'Exibição';
    if (t.includes('wta')) return 'WTA';
    if (t.includes('itf') || t.includes('challenger')) return 'ITF';
    return 'ATP';
  },

  _detectMatchCategory(type: string): 'Simples Masculino' | 'Simples Feminino' | 'Duplas Masculinas' | 'Duplas Femininas' | 'Duplas Mistas' | 'Copa por Equipes' | 'Masculino Juvenil' | 'Feminino Juvenil' | 'Outros' {
    if (!type) return 'Simples Masculino';
    const t = type.toLowerCase();
    if (t.includes('team') || t.includes('cup')) return 'Copa por Equipes';
    if (t.includes('mixed doubles')) return 'Duplas Mistas';
    
    if (t.includes('boys')) return 'Masculino Juvenil';
    if (t.includes('girls')) return 'Feminino Juvenil';
    
    if (t.includes('women doubles') || t.includes('wta doubles') || t.includes('itf women doubles')) return 'Duplas Femininas';
    if (t.includes('men doubles') || t.includes('atp doubles') || t.includes('itf men doubles') || t.includes('doubles')) return 'Duplas Masculinas';
    
    if (t.includes('women singles') || t.includes('wta singles') || t.includes('itf women singles') || t.includes('women')) return 'Simples Feminino';
    if (t.includes('men singles') || t.includes('atp singles') || t.includes('itf men singles') || t.includes('men') || t.includes('singles')) return 'Simples Masculino';
    
    return 'Outros';
  },

  _mapFixture(data: any): Fixture {
    const homeScores: number[] = [];
    const awayScores: number[] = [];
    
    if (data.scores && Array.isArray(data.scores)) {
      data.scores.forEach((s: any) => {
        homeScores.push(Math.floor(parseFloat(s.score_first || '0')));
        awayScores.push(Math.floor(parseFloat(s.score_second || '0')));
      });
    }

    const liveStats = {
      currentSet: data.scores?.length || 1,
      homeAces: 0, awayAces: 0,
      homeDoubleFaults: 0, awayDoubleFaults: 0,
      homeBreakPoints: 0, awayBreakPoints: 0,
    };

    if (data.statistics && Array.isArray(data.statistics)) {
      data.statistics.forEach((stat: any) => {
        if (stat.stat_period !== 'match') return; // Only take the match totals
        
        const val = stat.stat_won !== null ? stat.stat_won : parseInt(stat.stat_value) || 0;
        
        if (stat.player_key === data.first_player_key) {
          if (stat.stat_name === 'Aces') liveStats.homeAces += val;
          if (stat.stat_name === 'Double Faults') liveStats.homeDoubleFaults += val;
          if (stat.stat_name === 'Break Points Converted') liveStats.homeBreakPoints += val;
        } else if (stat.player_key === data.second_player_key) {
          if (stat.stat_name === 'Aces') liveStats.awayAces += val;
          if (stat.stat_name === 'Double Faults') liveStats.awayDoubleFaults += val;
          if (stat.stat_name === 'Break Points Converted') liveStats.awayBreakPoints += val;
        }
      });
    }

    let dateTime = data.event_date;
    if (data.event_time) dateTime += `T${data.event_time}`;

    let parsedRound = data.tournament_round || '';
    if (data.event_qualification === 'True') {
      if (parsedRound.toLowerCase().includes('quarter') || parsedRound.includes('1/4')) {
        parsedRound = 'Qualifying 1st Round';
      } else if (parsedRound.toLowerCase().includes('semi') || parsedRound.includes('1/2')) {
        parsedRound = 'Qualifying 2nd Round';
      } else if (parsedRound.toLowerCase().includes('final')) {
        parsedRound = 'Qualifying Final Round';
      } else {
        parsedRound = 'Qualifying Round';
      }
    }

    return {
      id: data.event_key,
      tournamentName: data.tournament_name || 'Torneio',
      tournamentKey: data.tournament_key,
      round: parsedRound,
      season: data.tournament_season,
      category: this._detectCategory(data.event_type_type),
      matchCategory: this._detectMatchCategory(data.event_type_type),
      status: data.event_status === 'Finished' ? 'Encerrado' : (data.event_live === '1' ? '🔴 Ao Vivo' : (data.event_time || 'Agendado')),
      date: dateTime,
      homePlayer: {
        id: data.event_home_team_key || data.first_player_key,
        name: data.event_home_team || data.event_first_player || 'Desconhecido',
        country: this._getFlagUrl(data.event_home_team_key || data.first_player_key, data.event_home_team_logo || data.event_first_player_logo, data.event_home_team || data.event_first_player),
        logo: data.event_home_team_logo || data.event_first_player_logo
      },
      awayPlayer: {
        id: data.event_away_team_key || data.second_player_key,
        name: data.event_away_team || data.event_second_player || 'Desconhecido',
        country: this._getFlagUrl(data.event_away_team_key || data.second_player_key, data.event_away_team_logo || data.event_second_player_logo, data.event_away_team || data.event_second_player),
        logo: data.event_away_team_logo || data.event_second_player_logo
      },
      score: {
        home: homeScores,
        away: awayScores,
        liveGameScore: data.event_game_result
      },
      liveStats
    };
  },

  async getTodayFixtures(): Promise<Fixture[]> {
    const today = new Date().toISOString().split('T')[0];
    return this.getFixturesByDate(today);
  },

  async getFixturesByDate(date: string): Promise<Fixture[]> {
    const data = await this._fetch('get_fixtures', { date_start: date, date_stop: date });
    if (!data.success || !data.result) return [];
    return data.result.map((f: any) => this._mapFixture(f));
  },

  async getLiveFixtures(): Promise<Fixture[]> {
    const data = await this._fetch('get_livescore');
    if (!data.success || !data.result) return [];
    return data.result.map((f: any) => this._mapFixture(f));
  },

  async getStandings(type: 'ATP' | 'WTA'): Promise<any[]> {
    const data = await this._fetch('get_standings', { event_type: type });
    if (!data.success || !data.result) return [];
    return data.result.map((s: any) => ({
      id: s.player_key,
      rank: s.place,
      name: s.player,
      points: s.points,
      country: s.country || '',
      flagUrl: this._getFlagUrl(s.player_key, '', s.player),
      movement: s.movement === 'up' ? 1 : s.movement === 'down' ? -1 : 0,
    }));
  },

  async getHeadToHead(player1Id: number, player2Id: number): Promise<any> {
    return await this._fetch('get_H2H', { first_player_key: player1Id.toString(), second_player_key: player2Id.toString() });
  },

  async getTournaments(): Promise<any[]> {
    const data = await this._fetch('get_tournaments');
    return data.success ? data.result : [];
  },

  async getTournamentFixtures(tournamentKey: string): Promise<Fixture[]> {
    const data = await this._fetch('get_fixtures', { tournament_key: tournamentKey });
    if (!data.success || !data.result) return [];
    return data.result.map((f: any) => this._mapFixture(f));
  },

  async getNews(): Promise<any[]> {
    const data = await this._fetch('get_news');
    return data.success ? data.result : [];
  },

  async getOdds(matchKey: string): Promise<any[]> {
    const data = await this._fetch('get_odds', { match_key: matchKey });
    return data.success && Array.isArray(data.result) ? data.result : (data.success && data.result ? Object.values(data.result) : []);
  },

  async getLiveOdds(): Promise<any[]> {
    const data = await this._fetch('get_live_odds');
    return data.success && Array.isArray(data.result) ? data.result : (data.success && data.result ? Object.values(data.result) : []);
  },

  async getPlayerStats(playerId: number): Promise<any> {
    const data = await this._fetch('get_players', { player_key: playerId.toString() });
    if (data.success && data.result.length > 0) {
      const p = data.result[0];
      return {
        id: p.player_key,
        name: p.player_name,
        country: p.player_country,
        stats: p.stats || []
      };
    }
    return null;
  }
};
